import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Spanish } from "flatpickr/dist/l10n/es.js"

// Connects to data-controller="turnos-datepicker"
export default class extends Controller {
  static targets = [ "input", "calendar", "timeInput", "timeSelectWrapper", "consultorioSelect", "profesionalHidden", "horariosContainer"]

  static values = { horariosMap: Object, autoSubmit: Boolean }

  connect() {
    const calendarEl = this.hasCalendarTarget ? this.calendarTarget : this.element

    this.initialDateStr = this.hasInputTarget ? (this.inputTarget.value || "") : ""
    this.initialTimeStr = this.hasTimeInputTarget ? (this.timeInputTarget.value || "") : ""

    this.picker = flatpickr(calendarEl, {
      inline: true,
      mode: "single",
      dateFormat: "Y-m-d",
      locale: Spanish,
      weekStartDay: 1,
      minDate: "today",
      onChange: (selectedDates) => {
        const dates = selectedDates.map(d => d.toISOString().slice(0, 10))
        if (this.hasInputTarget) this.inputTarget.value = dates.join(',')
        this.dispatch("selected", { detail: { dates: dates } })

        if (selectedDates.length > 0) {
          this.generateTimeSlots(selectedDates[0])
        } else {
          this.hideTimeSelect()
        }

        try {
          if (this.autoSubmitValue && dates.length > 0) {
            this.updateAdminTable(dates[0])
          }
        } catch (e) {
          console.error("Error auto-updating admin table", e)
        }
      }
    })

    const select = this.hasConsultorioSelectTarget
      ? this.consultorioSelectTarget
      : this.element.querySelector('select[data-action*="turnos-datepicker#consultorioChanged"]')

    // si estamos EDITANDO (ya viene profesional cargado)
    if (this.hasProfesionalHiddenTarget && this.profesionalHiddenTarget.value) {
      this.loadProfesionalData(this.profesionalHiddenTarget.value, { keepSelection: true })
        .then(() => this.applyInitialState())
        .catch(e => console.error("Error initial profesional load", e))
    } else {
      // caso NUEVO: si tuviera ya días cargados
      if (select && select.value && select.dataset.consultoriosDias) {
        this.applyAllowedDaysFromSelect(select)
      }
    }
  }

  // aplica estado inicial en edición (fecha + horarios)
  applyInitialState() {
    if (!this.picker || !this.initialDateStr) return

    const dateObj = this.picker.parseDate(this.initialDateStr, "Y-m-d")
    if (!dateObj) return

    // setear fecha sin disparar onChange
    this.picker.setDate(dateObj, false)

    const select = this.hasConsultorioSelectTarget ? this.consultorioSelectTarget : null
    if (select && select.value && select.dataset.consultoriosDias) {
      this.applyAllowedDaysFromSelect(select)
    }

    // generar horarios del día y preseleccionar la hora del turno
    this.generateTimeSlots(dateObj, { preselect: this.initialTimeStr })
  }

  // cuando cambia el consultorio
  consultorioChanged(event) {
    const select = event.currentTarget
    this.applyAllowedDaysFromSelect(select)

    // clear any previously selected dates
    if (this.picker) {
      this.picker.clear()
      if (this.hasInputTarget) this.inputTarget.value = ""
    }

    this.hideTimeSelect()
    if (this.hasHorariosContainerTarget) this.horariosContainerTarget.innerHTML = ""
    // ya no estamos en estado inicial
    this.initialDateStr = ""
    this.initialTimeStr = ""
  }

  // habilita/deshabilita días del calendario según consultorio
  applyAllowedDaysFromSelect(select) {
    let map = {}
    try {
      map = select.dataset.consultoriosDias ? JSON.parse(select.dataset.consultoriosDias) : {}
    } catch (e) {
      console.error("Error parsing consultorios_dias JSON", e)
      map = {}
    }

    if (!this.picker) return
    if (!map || Object.keys(map).length === 0) return

    const selectedId = select.value
    const allowed = (selectedId && map[selectedId]) ? map[selectedId] : []

    if (!allowed || allowed.length === 0) {
      this.picker.set("enable", [function () { return false }])
    } else {
      this.picker.set("enable", [function (date) {
        return allowed.indexOf(date.getDay()) !== -1
      }])
    }
  }

  showTimeSelect() {
    if (!this.hasTimeSelectWrapperTarget) return
    this.timeSelectWrapperTarget.classList.remove("d-none")
  }

  hideTimeSelect() {
    if (!this.hasTimeSelectWrapperTarget) return
    this.timeSelectWrapperTarget.classList.add("d-none")
    const select = this.timeSelectWrapperTarget.querySelector("select")
    if (select) select.innerHTML = ""
  }

  // Genera horas (intervalos de 15 min) y marca reservadas
  async generateTimeSlots(date, options = {}) {
    const dayOfWeek = date.getDay() // 0=Domingo...
    const consultorioSelect = this.hasConsultorioSelectTarget
      ? this.consultorioSelectTarget
      : this.element.querySelector('select[name*="consultorio_id"]')

    const consultorioId = consultorioSelect ? consultorioSelect.value : null

    if (!consultorioId) {
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) this.horariosContainerTarget.innerHTML = ""
      return
    }

    const dayMap = this.horariosMapValue[consultorioId]
    const ranges = dayMap ? dayMap[dayOfWeek] : null

    if (!ranges || ranges.length === 0) {
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) {
        this.horariosContainerTarget.innerHTML = "<p class='text-danger mb-0'>No hay horarios disponibles para este día.</p>"
      }
      return
    }

    const dateStr = date.toISOString().slice(0, 10)
    let reserved = []

    try {
      const resp = await fetch(`/consultorios/${consultorioId}/horarios_reservados?fecha=${dateStr}`)
      if (resp.ok) {
        const json = await resp.json()
        reserved = json.reservados || []
      } else {
        console.error("Error fetching reserved times", resp.status)
      }
    } catch (e) {
      console.error("Error fetching reserved times", e)
    }

    if (!this.hasTimeSelectWrapperTarget) return
    const select = this.timeSelectWrapperTarget.querySelector("select")
    if (!select) return

    const preselect = options.preselect || (this.hasTimeInputTarget ? this.timeInputTarget.value : null)

    select.innerHTML = "<option value=''>Seleccione una hora</option>"

    // Generar opciones cada 15 min, dejando libre la hora actual del turno aunque esté en reserved
    ranges.forEach(range => {
      let start = new Date(`2000/01/01 ${range.inicio}`)
      const end = new Date(`2000/01/01 ${range.fin}`)

      while (start <= end) {
        const hourString = start.toTimeString().slice(0, 5) // "HH:MM"
        const isReserved = reserved.includes(hourString) && hourString !== preselect

        if (!isReserved) {
          const option = document.createElement("option")
          option.value = hourString
          option.textContent = hourString
          select.appendChild(option)
        }

        // Sumar 15 minutos
        start.setMinutes(start.getMinutes() + 15)
      }
    })

    const totalOptions = select.options.length - 1 // sin contar el placeholder

    if (totalOptions <= 0) {
      // ni siquiera la hora actual está disponible
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) {
        this.horariosContainerTarget.innerHTML = "<p class='text-danger mb-0'>No hay horarios disponibles para este día.</p>"
      }
      return
    }

    this.showTimeSelect()

    // Preseleccionar la hora del turno si existe en la lista
    if (preselect) {
      const found = Array.from(select.options).find(opt => opt.value === preselect)
      if (found) {
        select.value = preselect
        if (this.hasTimeInputTarget) this.timeInputTarget.value = preselect
      }
    }

    // Mensaje si no hay otros horarios además del seleccionado
    if (this.hasHorariosContainerTarget) {
      if (totalOptions === 1 && preselect) {
        this.horariosContainerTarget.innerHTML = "<p class='text-muted mb-0'>No hay otros horarios disponibles para este día.</p>"
      } else {
        this.horariosContainerTarget.innerHTML = ""
      }
    }
  }

  timeSelected(event) {
    if (this.hasTimeInputTarget) {
      this.timeInputTarget.value = event.currentTarget.value
    }
  }

  async updateAdminTable(dateStr) {
    try {
      const url = new URL(window.location.href)
      url.pathname = "/turnos"
      url.searchParams.set("date", dateStr)

      const resp = await fetch(url.toString(), { credentials: "same-origin" })
      if (!resp.ok) {
        console.error("Error fetching admin table", resp.status)
        return
      }

      const html = await resp.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")

      const newFrame = doc.getElementById("turnos_administracion_table") ||
        doc.querySelector("turbo-frame#turnos_administracion_table") ||
        doc.querySelector('[data-turbo-frame][id="turnos_administracion_table"]')

      const currentFrame = document.getElementById("turnos_administracion_table") ||
        document.querySelector("turbo-frame#turnos_administracion_table") ||
        document.querySelector('[data-turbo-frame][id="turnos_administracion_table"]')

      if (newFrame && currentFrame) {
        currentFrame.innerHTML = newFrame.innerHTML
      } else if (currentFrame) {
        const newTable = doc.querySelector(".turnos-list")
        if (newTable) currentFrame.innerHTML = newTable.outerHTML
        else console.warn("Could not find replacement fragment in fetched HTML")
      } else {
        console.warn("No target frame found on page to update")
      }
    } catch (e) {
      console.error("Error in updateAdminTable", e)
    }
  }

  async profesionalSelected(event) {
    const valueFromEvent = event.detail && event.detail.value
    const matricula = valueFromEvent || (this.hasProfesionalHiddenTarget ? this.profesionalHiddenTarget.value : null)
    if (!matricula) return

    if (this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = matricula
    }

    await this.loadProfesionalData(matricula)
  }

  async profesionalChanged(event) {
    const matricula = event.target.value
    if (!matricula) return
    await this.loadProfesionalData(matricula)
  }

  // carga consultorios y mapas de días/horarios
  async loadProfesionalData(matricula, opts = {}) {
    const keepSelection = opts.keepSelection

    try {
      const resp = await fetch(`/profesionales/${encodeURIComponent(matricula)}/consultorios`)
      const consultorios = resp.ok ? await resp.json() : []
      this.updateConsultorios(consultorios, { keepSelection })

      const resp2 = await fetch(`/profesionales/${encodeURIComponent(matricula)}/consultorios_info`)
      const info = resp2.ok ? await resp2.json() : { consultorios_dias: {}, horarios_map: {} }

      if (this.hasConsultorioSelectTarget) {
        this.consultorioSelectTarget.dataset.consultoriosDias = JSON.stringify(info.consultorios_dias)
      }
      this.horariosMapValue = info.horarios_map || {}

      if (!keepSelection) {
        if (this.picker) this.picker.clear()
        if (this.hasInputTarget) this.inputTarget.value = ""
        this.hideTimeSelect()
        if (this.hasHorariosContainerTarget) this.horariosContainerTarget.innerHTML = ""
      }
    } catch (e) {
      console.error("Error loading profesional data", e)
    }
  }

  updateConsultorios(consultorios, opts = {}) {
    if (!this.hasConsultorioSelectTarget) return
    const select = this.consultorioSelectTarget
    const currentValue = opts.keepSelection ? select.value : ""

    select.innerHTML = "<option value=''>Seleccione un consultorio</option>"
    consultorios.forEach(c => {
      const option = document.createElement("option")
      option.value = String(c.id)
      option.textContent = c.nombre
      select.appendChild(option)
    })

    if (opts.keepSelection && currentValue) {
      select.value = currentValue
    }
  }
}
